import isEqual from 'lodash-es/isEqual';
import * as moment from 'moment';

import { type FilterCriteria, type JSONFilterGroup } from '~/types/FilterGroup';
import * as order from '~/types/Order';

const ORDER_ITEM_QUANTITY_FIELD = 'order_item_quantity';

const parseFilterGroups = (show: string, filterGroups: JSONFilterGroup[]) => {
  if (!filterGroups) return {};

  const newFilters: Record<string, unknown> = {};

  const filterGroup = filterGroups.find((filterGroup) => filterGroup.name === show);
  if (filterGroup && filterGroup.filter_criteria) {
    filterGroup.filter_criteria.forEach((filterCriteria: FilterCriteria) => {
      const filter = filterCriteria.filter;
      if (filter.field === 'ORDER_BY') newFilters['order_by'] = filter.value.text;

      if (filter.field === 'SORT_BY') newFilters['sort_by'] = filter.value.text;

      if (filter.field === 'WEIGHT') {
        const comparator = filter.comparator === 'less_than' ? 'lt' : 'gt';
        newFilters[`weight_${comparator}`] = filter.value.value;
        newFilters['weight_unit'] = filter.value.enum;
      }

      if (
        filter.value.value !== null &&
        (filter.field === 'TOTAL_ITEM_QUANTITY' ||
          filter.field === 'ORDER_ITEM_QUANTITY' ||
          filter.field === ORDER_ITEM_QUANTITY_FIELD)
      ) {
        if (filter.comparator === 'less_than' || filter.comparator === 'less_than_or_equal_to')
          newFilters['total_item_quantity_max'] = filter.value.value;
        else if (
          filter.comparator === 'greater_than' ||
          filter.comparator === 'greater_than_or_equal_to'
        )
          newFilters['total_item_quantity_min'] = filter.value.value;
      }

      if (filter.field === 'SOURCE') newFilters['source'] = filter.value.set?.sort();

      if (filter.field === 'STORE_PLATFORM') {
        if (filter.value.set && filter.value.set.length > 0)
          newFilters['shop_app'] = filter.value.set[0];
      }
      if (filter.field === 'IS_INTERNATIONAL')
        newFilters['is_international'] = filter.value.boolean?.toString();

      if (filter.field === 'DATE_CREATED') {
        if (filter.comparator === 'greater_than_or_equal_to')
          newFilters['start_date'] = moment(filter.value.date).format();
        else if (filter.comparator === 'less_than_or_equal_to')
          newFilters['end_date'] = moment(filter.value.date).format();
      }
      if (filter.field === 'SHIPPING_METHOD') newFilters['shipping_method'] = filter.value.set;

      if (filter.field === 'ADDRESS_FROM' || filter.field === 'address_from')
        newFilters['address_from'] = filter.value.set?.slice().sort();

      if (filter.field === 'ITEM') newFilters['item'] = filter.value.set?.sort();

      if (filter.field === 'ORDER_STATUS') {
        if (filter.value.set && filter.value.set.length > 0)
          newFilters['order_status'] = filter.value.set.length > 1 ? 'pending' : 'fulfilled';
      }
      if (filter.field === 'SEARCH_TEXT') newFilters['q'] = filter.value.text;

      if (filter.field === 'TAG' && filter.value.set) {
        const untaggedIndex = filter.value.set.indexOf('untagged');
        if (untaggedIndex === -1) {
          newFilters['tag'] = filter.value.set;
          newFilters['untagged'] = 'false';
        } else {
          const copyTagSet = [...filter.value.set];
          copyTagSet.splice(untaggedIndex, 1);
          newFilters['untagged'] = 'true';
          if (copyTagSet.length > 0) newFilters['tag'] = copyTagSet;
        }
      }
    });
  }

  return newFilters;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseFilters = (filters: any): any[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterCriteria: any[] = [];
  if ('weight_unit' in filters) {
    const weightFilter = {
      filter: {
        field: 'weight',
        value: {
          enum: filters.weight_unit,
        },
      },
    };
    if ('weight_lt' in filters) {
      filterCriteria.push({
        filter: {
          ...weightFilter.filter,
          comparator: 'less_than',
          value: {
            ...weightFilter.filter.value,
            value: filters.weight_lt,
          },
        },
      });
    }
    if ('weight_gt' in filters) {
      filterCriteria.push({
        filter: {
          ...weightFilter.filter,
          comparator: 'greater_than',
          value: {
            ...weightFilter.filter.value,
            value: filters.weight_gt,
          },
        },
      });
    }
  }

  if ('source' in filters) {
    const sourcesSet: string[] = [];
    if (Array.isArray(filters['source'])) sourcesSet.push(...filters['source']);
    else if (typeof filters['source'] === 'string') sourcesSet.push(filters['source']);
    else if (typeof filters['source'] === 'object')
      sourcesSet.push(...(Object.values(filters['source']) as string[]));

    filterCriteria.push({
      filter: {
        comparator: 'in',
        field: 'source',
        value: {
          set: sourcesSet,
        },
      },
    });
  }

  if ('shop_app' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'in',
        field: 'store_platform',
        value: {
          set: [filters['shop_app']],
        },
      },
    });
  }

  if ('is_international' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'equal_to',
        field: 'is_international',
        value: {
          boolean: filters['is_international'] === 'true',
        },
      },
    });
  }

  if ('start_date' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'greater_than_or_equal_to',
        field: 'date_created',
        value: {
          date: filters['start_date'],
        },
      },
    });
  }

  if ('end_date' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'less_than_or_equal_to',
        field: 'date_created',
        value: {
          date: filters['end_date'],
        },
      },
    });
  }

  if ('shipping_method' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'in',
        field: 'shipping_method',
        value: {
          set: filters['shipping_method'],
        },
      },
    });
  }

  if ('address_from' in filters) {
    const addressFromSet: string[] = [];
    if (Array.isArray(filters['address_from'])) addressFromSet.push(...filters['address_from']);
    else if (typeof filters['address_from'] === 'string')
      addressFromSet.push(filters['address_from']);
    else if (typeof filters['address_from'] === 'object')
      addressFromSet.push(...(Object.values(filters['address_from']) as string[]));

    filterCriteria.push({
      filter: {
        comparator: 'in',
        field: 'address_from',
        value: {
          set: addressFromSet,
        },
      },
    });
  }

  if ('item' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'in',
        field: 'item',
        value: {
          set: filters['item'],
        },
      },
    });
  }

  if (
    'total_item_quantity_max' in filters &&
    filters['total_item_quantity_max'] !== undefined &&
    filters['total_item_quantity_max'] !== ''
  ) {
    filterCriteria.push({
      filter: {
        comparator: 'less_than_or_equal_to',
        field: ORDER_ITEM_QUANTITY_FIELD,
        value: {
          value: filters['total_item_quantity_max'],
        },
      },
    });
  }

  if (
    'total_item_quantity_min' in filters &&
    filters['total_item_quantity_min'] !== undefined &&
    filters['total_item_quantity_min'] !== ''
  ) {
    filterCriteria.push({
      filter: {
        comparator: 'greater_than_or_equal_to',
        field: ORDER_ITEM_QUANTITY_FIELD,
        value: {
          value: filters['total_item_quantity_min'],
        },
      },
    });
  }

  if ('order_status' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'in',
        field: 'ORDER_STATUS',
        value: {
          set:
            filters['order_status'] === 'pending'
              ? [
                  order.OrderStatus.PAID,
                  order.OrderStatus.UNKNOWN,
                  order.OrderStatus.AWAITPAY,
                  order.OrderStatus.PARTIALLY_FULFILLED,
                ]
              : [order.OrderStatus.SHIPPED],
        },
      },
    });
  }

  if ('q' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'equal_to',
        field: 'search_text',
        value: {
          text: filters['q'],
        },
      },
    });
  }
  if ('sort_by' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'equal_to',
        field: 'sort_by',
        value: {
          text: filters['sort_by'],
        },
      },
    });
  }
  if ('order_by' in filters) {
    filterCriteria.push({
      filter: {
        comparator: 'equal_to',
        field: 'order_by',
        value: {
          text: filters['order_by'],
        },
      },
    });
  }

  if ('tag' in filters || ('untagged' in filters && filters['untagged'] === 'true')) {
    const tagsSet: string[] = [];
    if ('tag' in filters) {
      if (Array.isArray(filters['tag'])) tagsSet.push(...filters['tag']);
      else if (typeof filters['tag'] === 'string') tagsSet.push(filters['tag']);
      else if (typeof filters['tag'] === 'object')
        tagsSet.push(...(Object.values(filters['tag']) as string[]));
    }
    if ('untagged' in filters && filters['untagged'] === 'true') tagsSet.push('untagged');

    filterCriteria.push({
      filter: {
        comparator: 'in',
        field: 'tag',
        value: {
          set: tagsSet,
        },
      },
    });
  }

  return filterCriteria;
};

const getTabName = (
  nonEmptyFilterValues: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any>,
  savedTabName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterGroups: any,
) => {
  // TODO: This looks like a bug. The usage suggests it wants an array but it is always passed an object.
  const savedFilterGroup =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterGroups.find((filterGroup: any) => filterGroup.name === savedTabName) || {};
  const parsedFilters = parseFilterGroups(savedTabName, [savedFilterGroup]);

  const cleanedFilters = Object.keys(filters).reduce((cleanFilters, filterName) => {
    if (filters[filterName]) {
      return {
        ...cleanFilters,
        [filterName]: filters[filterName],
      };
    }
    return cleanFilters;
  }, {});

  if (nonEmptyFilterValues.length === 0) return savedTabName;
  if (isEqual(cleanedFilters, parsedFilters)) return savedTabName;

  // We need to add another case here to check if the filters are changed due to pagination
  // When filters are changed due to pagination, we should not change the tab name
  // This is to fix this ticket - https://shippo.atlassian.net/browse/WEB-71
  // When we change page from the landing page to the next page, the tab name is changing to new
  // Because the of filter values `order_by=desc&sort_by=_score` is added to the URL
  // So we check for exactly those values and return the savedTabName which is the current tab name / undefined in this case
  if (
    nonEmptyFilterValues.length === 2 &&
    filters.order_by === 'desc' &&
    filters.sort_by === '_score'
  )
    return savedTabName;

  return 'new';
};

/**
  @description remove SEARCH_TEXT filter query from filter_criteria list if value text is empty.
 */
const cleanFilterCriteria = (filters: JSONFilterGroup[]) =>
  filters.map((filter) => {
    const removeEmptySearchText = filter.filter_criteria.filter(
      (criteria) =>
        criteria.filter.field !== 'SEARCH_TEXT' ||
        (criteria.filter.field === 'SEARCH_TEXT' && criteria.filter.value.text),
    );
    return { ...filter, filter_criteria: removeEmptySearchText };
  });

export { cleanFilterCriteria, getTabName, parseFilterGroups, parseFilters };
